import React, {
  memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dropdown,
  DropdownMenu,
  DropdownToggle,
} from 'reactstrap';

import LanguageDropdown from 'Layout/AuthLayout/HeaderDropdown/LanguageDropdown';

import { onClickLink } from 'utils/permission';
import {
  filterExtranetNavigation,
  getExtranetPermissionByPlace,
} from 'utils/extranetPermission';
import cn from 'utils/classnames';
import { socketOn } from 'utils/socket';

import { ReactComponent as ArrowHeaderIcon } from 'assets/images/headerNavigation/arrow.svg';
import images from 'assets/images';
import { LOCATIONS } from 'constants/index';
import headerExtranetNavigation from 'constants/headerExtranetNavigation';
import eyeIcon from 'assets/images/eye-icon.svg';
// import questionIcon from 'assets/images/Question.svg';

import ExtranetProfileMenu from '../ExtranetProfileMenu';
import PlaceDropdown from '../PlaceDropdown/PlaceDropdown';

import styles from './HeaderExtranet.module.scss';

const resolvePlaceId = (places, urlPlaceId, storedPlaceId) => {
  const isValidPlaceId = (placeId) => places.some((place) => place._id === placeId);

  if (storedPlaceId && isValidPlaceId(storedPlaceId)) {
    return storedPlaceId;
  }

  if (urlPlaceId && isValidPlaceId(urlPlaceId)) {
    return urlPlaceId;
  }

  return places[0]?._id;
};

const applyExtranetNotifications = (navigation, extranetNotification) => {
  if (!extranetNotification) {
    return navigation;
  }

  return navigation.map((item) => {
    if (!item.notificationKey) {
      return item;
    }

    return {
      ...item,
      hasNotification: extranetNotification[item.notificationKey],
    };
  });
};

const NavDropdown = ({
  item,
  isActive,
  searchQuery,
  onClickLinkItem,
  onReadNotification,
  t,
}) => {
  const location = useLocation();
  const IconComponent = item.icon;
  const [menu, setMenu] = useState(false);

  const onToggle = useCallback(() => {
    setMenu((prevState) => {
      const isOpening = !prevState;

      if (isOpening) {
        onReadNotification(item);
      }

      return isOpening;
    });
  }, [item, onReadNotification]);

  return (
    <Dropdown
      isOpen={menu}
      toggle={onToggle}
      className={styles.NavDropdown}
    >
      <DropdownToggle
        tag="button"
        className={cn(styles.NavItem, isActive && styles.active)}
      >
        <span className={styles.NavItemIcon}>
          <IconComponent />
          {item.hasNotification && <span className={styles.NotificationBadge} />}
        </span>

        <span className={styles.NavItemLabel}>
          {t(item.name)}
          <span className={styles.NavItemArrow}>
            <ArrowHeaderIcon />
          </span>
        </span>
      </DropdownToggle>

      <DropdownMenu className={cn(styles.NavDropdownMenu, menu && styles.NavDropdownMenuOpen)}>
        {item.children.map((child) => {
          const isChildActive = child.link === location.pathname;

          return (
            <Link
              key={child.name}
              to={{
                pathname: child.link,
                search: searchQuery.place ? `?place=${searchQuery.place}` : '',
              }}
              className={cn(
                'dropdown-item',
                styles.NavDropdownItem,
                isChildActive && styles.active,
              )}
              onClick={onClickLinkItem}
            >
              {t(child.name)}
            </Link>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );
};

NavDropdown.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    hasNotification: PropTypes.bool,
    children: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  isActive: PropTypes.bool.isRequired,
  searchQuery: PropTypes.shape({
    place: PropTypes.string,
  }).isRequired,
  onClickLinkItem: PropTypes.func.isRequired,
  onReadNotification: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const HeaderExtranet = ({
  myPermission,
  myPlace,
  isLoadingGetMyPlace,
  actionGetMyPlace,
  actionGetExtranetNotification,
  actionReadExtranetNotification,
  actionSyncExtranetNotification,
  extranetNotification,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (!myPlace.length) {
      actionGetMyPlace();
    }
  }, [actionGetMyPlace, myPlace.length]);

  const searchQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      place: params.get('place'),
    };
  }, [location.search]);

  const currentPlace = useMemo(
    () => myPlace.find((place) => place._id === searchQuery.place) ?? myPlace[0],
    [myPlace, searchQuery.place],
  );

  const extranetPermission = useMemo(
    () => getExtranetPermissionByPlace(myPermission, searchQuery.place),
    [myPermission, searchQuery.place],
  );

  const visibleNavigation = useMemo(
    () => applyExtranetNotifications(
      filterExtranetNavigation(headerExtranetNavigation, extranetPermission),
      extranetNotification,
    ),
    [extranetNotification, extranetPermission],
  );

  const onSelectPlace = useCallback((placeId) => {
    localStorage.setItem('place', placeId);

    const params = new URLSearchParams(location.search);
    params.set('place', placeId);

    history.push({
      pathname: location.pathname,
      search: `?${params.toString()}`,
    });
  }, [history, location.pathname, location.search]);

  useEffect(() => {
    if (isLoadingGetMyPlace || !myPlace.length) {
      return;
    }

    const storedPlaceId = localStorage.getItem('place');
    const resolvedPlaceId = resolvePlaceId(
      myPlace,
      searchQuery.place,
      storedPlaceId,
    );

    if (!resolvedPlaceId) {
      return;
    }

    if (extranetNotification?.place === resolvedPlaceId) {
      return;
    }

    actionGetExtranetNotification(resolvedPlaceId);
  }, [
    actionGetExtranetNotification,
    extranetNotification,
    isLoadingGetMyPlace,
    myPlace,
    searchQuery.place,
  ]);

  useEffect(() => {
    if (!searchQuery.place) {
      return () => {};
    }

    const unsubscribe = socketOn('extranet_notification', (eventPayload) => {
      const notification = eventPayload?.notification;

      if (!notification?.place || notification.place !== searchQuery.place) {
        return;
      }

      actionSyncExtranetNotification(notification);
    });

    return unsubscribe;
  }, [actionSyncExtranetNotification, searchQuery.place]);

  useEffect(() => {
    if (isLoadingGetMyPlace || !myPlace.length) {
      return;
    }

    const storedPlaceId = localStorage.getItem('place');
    const resolvedPlaceId = resolvePlaceId(
      myPlace,
      searchQuery.place,
      storedPlaceId,
    );

    if (!resolvedPlaceId) {
      return;
    }

    if (storedPlaceId !== resolvedPlaceId) {
      localStorage.setItem('place', resolvedPlaceId);
    }

    if (resolvedPlaceId !== searchQuery.place) {
      const params = new URLSearchParams(location.search);
      params.set('place', resolvedPlaceId);

      history.replace({
        pathname: location.pathname,
        search: `?${params.toString()}`,
      });
    }
  }, [
    history,
    isLoadingGetMyPlace,
    location.pathname,
    location.search,
    myPlace,
    searchQuery.place,
  ]);

  const onClickLinkItem = useCallback((e) => {
    onClickLink(e, myPermission);
  }, [myPermission]);

  const onReadNotification = useCallback((item) => {
    if (!item.notificationKey || !item.hasNotification || !searchQuery.place) {
      return;
    }

    actionReadExtranetNotification({
      place: searchQuery.place,
      [item.notificationKey]: true,
    });
  }, [actionReadExtranetNotification, searchQuery.place]);

  const onClickNavItem = useCallback((item) => (e) => {
    onClickLink(e, myPermission);
    onReadNotification(item);
  }, [myPermission, onReadNotification]);

  const isItemActive = useCallback((item) => {
    if (item.link) {
      return item.link === location.pathname;
    }

    if (item.children?.length) {
      return item.children.some((child) => child.link === location.pathname);
    }

    return false;
  }, [location.pathname]);

  const renderNavItem = useCallback((item) => {
    const IconComponent = item.icon;
    const isActive = isItemActive(item);

    if (item.children?.length > 0) {
      return (
        <NavDropdown
          key={item.name}
          item={item}
          isActive={isActive}
          searchQuery={searchQuery}
          onClickLinkItem={onClickLinkItem}
          onReadNotification={onReadNotification}
          t={t}
        />
      );
    }

    return (
      <Link
        key={item.name}
        to={{
          pathname: item.link,
          search: searchQuery.place ? `?place=${searchQuery.place}` : '',
        }}
        className={cn(styles.NavItem, isActive && styles.active)}
        onClick={onClickNavItem(item)}
      >
        <span className={styles.NavItemIcon}>
          <IconComponent />
          {item.hasNotification && <span className={styles.NotificationBadge} />}
        </span>

        <span className={styles.NavItemLabel}>{t(item.name)}</span>
      </Link>
    );
  }, [isItemActive, onClickLinkItem, onClickNavItem, onReadNotification, searchQuery, t]);

  const bookeseUrl = process.env.REACT_APP_BOOKESE_URL || 'https://bookese.wiicamp.xyz';

  return (
    <header className={styles.Component}>
      <div className={styles.Container}>
        <div className={styles.TopBar}>
          <Link to={LOCATIONS.HOME_PAGE} className={styles.Logo}>
            <img src={images.logoBookeseFull.default} alt="bookese" />
          </Link>

          <div className={styles.TopBarActions}>
            <PlaceDropdown
              myPlace={myPlace}
              isLoadingGetMyPlace={isLoadingGetMyPlace}
              selectedPlaceId={searchQuery.place}
              onSelectPlace={onSelectPlace}
            />
            <div className={styles.TopBarActionsIcons}>
              <a
                href={currentPlace?.slug ? `${bookeseUrl}/${currentPlace.slug}` : `${bookeseUrl}/`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.IconButton}
              >
                <img src={eyeIcon} alt="eye" />
              </a>

              {/* <button type="button" className={styles.IconButton}>
                <img src={questionIcon} alt="question" />
              </button> */}
            </div>

            <span className={styles.Divider} />

            <ExtranetProfileMenu />

            <span className={styles.Divider} />

            <div className={styles.LanguageDropdown}>
              <LanguageDropdown />
            </div>
          </div>
        </div>

        <nav className={styles.NavBar}>
          {visibleNavigation.map(renderNavItem)}
        </nav>
      </div>
    </header>
  );
};

HeaderExtranet.propTypes = {
  myPermission: PropTypes.instanceOf(Array),
  myPlace: PropTypes.instanceOf(Array),
  isLoadingGetMyPlace: PropTypes.bool,
  actionGetMyPlace: PropTypes.func.isRequired,
  actionGetExtranetNotification: PropTypes.func.isRequired,
  actionReadExtranetNotification: PropTypes.func.isRequired,
  actionSyncExtranetNotification: PropTypes.func.isRequired,
  extranetNotification: PropTypes.instanceOf(Object),
};

HeaderExtranet.defaultProps = {
  myPermission: [],
  myPlace: [],
  isLoadingGetMyPlace: false,
  extranetNotification: null,
};

export default memo(HeaderExtranet);
